import React, { useState, useEffect } from 'react';
import { useAuth } from '../../../contexts/AuthContext';
import Card from '../../common/Card.jsx';
import Pagination from '../../common/Pagination.jsx';
import { useTheme } from '../../../contexts/ThemeContext';

const ArticlesList = ({
  articles,
  loading,
  pagination,
  onPageChange,
  onPageSizeChange,
  onView,
  onEdit,
  onDelete
}) => {
  const { isDarkMode } = useTheme();
  const [viewMode, setViewMode] = useState('cards'); // Default to cards for better mobile experience
  const [isMobile, setIsMobile] = useState(window.innerWidth < 1024);

  // Handle window resize
  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 1024);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Get status based on article criteria
  const getStatus = (article) => {
    console.log('article:', article);
    return article.enService ? 'En service' : 'Hors service';
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'En service':
        return isDarkMode ? 'bg-green-900 text-green-300' : 'bg-green-100 text-green-800';
      case 'Maintenance':
        return isDarkMode ? 'bg-yellow-900 text-yellow-300' : 'bg-yellow-100 text-yellow-800';
      case 'Hors service':
        return isDarkMode ? 'bg-red-900 text-red-300' : 'bg-red-100 text-red-800';
      default:
        return isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-100 text-gray-800';
    }
  };

const getTypeData = (article) => {
  const pathImage = article.typeArticle?.pathImage;
  const designation = article.typeArticle?.designation;
  // Dot color logic
  const dotColor =
    designation === 'Poudre'
      ? 'bg-orange-400'
      : 'bg-blue-400';

  // Background color logic (light/dark)
  let bgColor;
  if (isDarkMode) {
    bgColor =
      designation === 'Poudre'
        ? 'bg-orange-900/40'
        : 'bg-blue-900/40';
  } else {
    bgColor =
      designation === 'Poudre'
        ? 'bg-orange-50'
        : 'bg-blue-50';
  }

  return {
    image: pathImage ? pathImage : '/assets/images/extinteur.jpg',
    dotColor,
    bgColor,
  };
};

  // Mobile Card Component - Enhanced with images, admin controls
  const { user } = useAuth();
  const ArticleCard = ({ article }) => {
    const typeData = getTypeData(article);
    return (
      <div className={`border rounded-xl overflow-hidden hover:shadow-lg transition-all duration-200 ${
        isDarkMode 
          ? 'bg-gray-800 border-gray-700 hover:border-dark-primary-600' 
          : 'bg-white border-gray-200 hover:border-primary-200'
      }`}>
        {/* Image Section */}
        <div className={`relative h-32 ${typeData.bgColor} flex items-center justify-center`}>
          <img 
            src={typeData.image}
            alt={article.typeArticle?.designation || 'Extincteur'}
            className="h-24 w-auto object-contain filter drop-shadow-sm"
            onError={(e) => {
              e.target.style.display = 'none';
              e.target.nextElementSibling.style.display = 'flex';
            }}
          />
          {/* Fallback icon */}
          <div 
            className="hidden w-16 h-16 bg-gray-200 rounded-full items-center justify-center"
            style={{ display: 'none' }}
          >
            <i className="fas fa-fire-extinguisher text-2xl text-gray-500"></i>
          </div>
          {/* Status Badge - Positioned on image */}
          <span className={`absolute top-2 right-2 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(getStatus(article))}`}>
            {getStatus(article)}
          </span>
        </div>
        {/* Content Section */}
        <div className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div className="flex-1 min-w-0">
              <h3 className={`text-lg font-semibold truncate ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{article.designation}</h3>
              <div className="flex items-center mt-1">
                <span className={`inline-block w-2 h-2 rounded-full mr-2 ${typeData.dotColor}`}></span>
                <span className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>{article.typeArticle?.designation || 'N/A'}</span>
              </div>
            </div>
          </div>
          <div className="space-y-2 mb-4">
            <div className="flex justify-between items-center text-sm">
              <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Capacité:</span>
              <span className={`font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>{article.capacite}kg</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Marque:</span>
              <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} truncate ml-2`}>{article.marque}</span>
            </div>
            <div className="flex justify-between items-start text-sm">
              <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Emplacement:</span>
              <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-900'} text-right ml-2 max-w-[60%]`}>{article.emplacement}</span>
            </div>
            <div className="flex justify-between items-center text-sm">
              <span className={`${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>Code Barre:</span>
              <code className={`px-2 py-1 rounded text-xs font-mono ${
                isDarkMode 
                  ? 'bg-gray-700 text-gray-300' 
                  : 'bg-gray-100 text-gray-900'
              }`}>
                {article.codeABarre}
              </code>
            </div>
          </div>
          <div className={`flex justify-between items-center pt-3 border-t ${
            isDarkMode ? 'border-gray-700' : 'border-gray-100'
          }`}>
            <div className={`text-xs truncate flex-1 ${
              isDarkMode ? 'text-gray-400' : 'text-gray-500'
            }`}>
              {article.local?.responsable && (
                <div className="flex items-center">
                  <i className="fas fa-user mr-1"></i>
                  <span className="truncate">{article.local.responsable}</span>
                </div>
              )}
            </div>
            <div className="flex space-x-1 ml-3">
              <button 
                onClick={() => onView(article)}
                className={`transition-colors p-2 rounded-lg ${
                  isDarkMode 
                    ? 'text-dark-primary-400 hover:text-dark-primary-300 hover:bg-gray-700' 
                    : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                }`}
                title="Voir détails"
              >
                <i className="fas fa-eye text-sm"></i>
              </button>
              {user?.isAdmin && (
                <>
                  <button 
                    onClick={() => onEdit(article)}
                    className={`transition-colors p-2 rounded-lg ${
                      isDarkMode 
                        ? 'text-green-400 hover:text-green-300 hover:bg-gray-700' 
                        : 'text-green-600 hover:text-green-800 hover:bg-green-50'
                    }`}
                    title="Modifier"
                  >
                    <i className="fas fa-edit text-sm"></i>
                  </button>
                  <button 
                    onClick={() => onDelete(article)}
                    className={`transition-colors p-2 rounded-lg ${
                      isDarkMode 
                        ? 'text-red-400 hover:text-red-300 hover:bg-gray-700' 
                        : 'text-red-600 hover:text-red-800 hover:bg-red-50'
                    }`}
                    title="Supprimer"
                  >
                    <i className="fas fa-trash text-sm"></i>
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  };

  return (
    <Card className="overflow-hidden">
      <div className={`px-4 py-3 sm:px-6 sm:py-4 border-b ${
        isDarkMode ? 'border-gray-700' : 'border-gray-200'
      }`}>
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <h2 className={`text-lg font-semibold ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>Liste des Articles</h2>
          
          {/* View Toggle - Only show on desktop */}
          {!isMobile && (
            <div className={`flex items-center space-x-2 rounded-lg p-1 ${
              isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
            }`}>
              <button
                onClick={() => setViewMode('table')}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                  viewMode === 'table'
                    ? isDarkMode
                      ? 'bg-gray-600 text-white shadow-sm'
                      : 'bg-white text-gray-900 shadow-sm'
                    : isDarkMode
                      ? 'text-gray-300 hover:text-white'
                      : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="fas fa-table mr-1"></i>
                Tableau
              </button>
              <button
                onClick={() => setViewMode('cards')}
                className={`px-3 py-1.5 rounded text-sm font-medium transition-colors ${
                  viewMode === 'cards'
                    ? 'bg-white text-gray-900 shadow-sm'
                    : 'text-gray-600 hover:text-gray-900'
                }`}
              >
                <i className="fas fa-th-large mr-1"></i>
                Cartes
              </button>
            </div>
          )}
        </div>
      </div>

      <div className="p-4 sm:p-6">
        
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <div className={`animate-spin rounded-full h-8 w-8 border-b-2 mr-4 ${
              isDarkMode ? 'border-dark-primary-600' : 'border-primary-600'
            }`}></div>
            <span className={isDarkMode ? 'text-gray-300' : 'text-gray-600'}>
              Chargement des articles...
            </span>
          </div>
        ) : (
          <>
            {/* Mobile Always Cards / Desktop Conditional */}
            {(isMobile || viewMode === 'cards') && (
              <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                {articles.map((article) => (
                  <ArticleCard key={article.id} article={article} />
                ))}
              </div>
            )}

            {/* Desktop Table View - Remains unchanged */}
            {!isMobile && viewMode === 'table' && (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead>
                    <tr className={`border-b border-gray-200 ${isDarkMode ? 'bg-gray-800' : ''}`}>
                      <th className={`text-left py-3 px-4 font-semibold whitespace-nowrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Désignation</th>
                      <th className={`text-left py-3 px-4 font-semibold whitespace-nowrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Type</th>
                      <th className={`text-left py-3 px-4 font-semibold whitespace-nowrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Capacité</th>
                      <th className={`text-left py-3 px-4 font-semibold whitespace-nowrap min-w-[200px] ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Local</th>
                      <th className={`text-left py-3 px-4 font-semibold whitespace-nowrap min-w-[150px] ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Emplacement</th>
                      <th className={`text-left py-3 px-4 font-semibold whitespace-nowrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Marque</th>
                      <th className={`text-left py-3 px-4 font-semibold whitespace-nowrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Code Barre</th>
                      <th className={`text-left py-3 px-4 font-semibold whitespace-nowrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Status</th>
                      <th className={`text-left py-3 px-4 font-semibold whitespace-nowrap ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {articles.map((article) => (
                      <tr key={article.id} className={`border-b transition-colors ${
                        isDarkMode 
                          ? 'border-gray-700 hover:bg-gray-700' 
                          : 'border-gray-100 hover:bg-gray-50'
                      }`}>
                        <td className={`py-3 px-4 font-medium ${
                          isDarkMode ? 'text-white' : 'text-gray-900'
                        }`}>{article.designation}</td>
                        <td className={`py-3 px-4 ${
                          isDarkMode ? 'text-gray-300' : 'text-gray-600'
                        }`}>
                          <div className="flex items-center whitespace-nowrap">
                            <span className={`inline-block w-3 h-3 rounded-full mr-2 ${
                              article.typeArticle?.designation === 'CO2' ? 'bg-gray-400' :
                              article.typeArticle?.designation === 'Poudre' ? 'bg-orange-400' : 'bg-blue-400'
                            }`}></span>
                            {article.typeArticle?.designation || 'N/A'}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-600 whitespace-nowrap">{article.capacite}kg</td>
                        <td className="py-3 px-4 text-gray-600">
                          <div className={`font-medium truncate ${
                            isDarkMode ? 'text-white' : 'text-gray-900'
                          }`}>{article.local?.responsable}</div>
                          <div className={`truncate max-w-[180px] ${
                            isDarkMode ? 'text-gray-400' : 'text-gray-500'
                          }`}>{article.local?.adresse}</div>
                        </td>
                        <td className="py-3 px-4 text-gray-600">
                          <div className="max-w-[140px] truncate" title={article.emplacement}>
                            {article.emplacement}
                          </div>
                        </td>
                        <td className="py-3 px-4 text-gray-600 whitespace-nowrap">{article.marque}</td>
                        <td className="py-3 px-4 text-gray-600">
                          <code className={`px-2 py-1 rounded text-xs font-mono whitespace-nowrap ${
                            isDarkMode 
                              ? 'bg-gray-700 text-gray-300' 
                              : 'bg-gray-100 text-gray-900'
                          }`}>
                            {article.codeABarre}
                          </code>
                        </td>
                        <td className="py-3 px-4">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium whitespace-nowrap ${getStatusColor(getStatus(article))}`}>
                            {getStatus(article)}
                          </span>
                        </td>
                        <td className="py-3 px-4">
                          <div className="flex space-x-2">
                            <button 
                              onClick={() => onView(article)}
                              className={`transition-colors p-1 rounded ${
                                isDarkMode 
                                  ? 'text-dark-primary-400 hover:text-dark-primary-300 hover:bg-gray-700' 
                                  : 'text-blue-600 hover:text-blue-800 hover:bg-blue-50'
                              }`}
                              title="Voir détails"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            <button 
                              onClick={() => onEdit(article)}
                              className={`transition-colors p-1 rounded ${
                                isDarkMode 
                                  ? 'text-green-400 hover:text-green-300 hover:bg-gray-700' 
                                  : 'text-green-600 hover:text-green-800 hover:bg-green-50'
                              }`}
                              title="Modifier"
                            >
                              <i className="fas fa-edit"></i>
                            </button>
                            <button 
                              onClick={() => onDelete(article)}
                              className={`transition-colors p-1 rounded ${
                                isDarkMode 
                                  ? 'text-red-400 hover:text-red-300 hover:bg-gray-700' 
                                  : 'text-red-600 hover:text-red-800 hover:bg-red-50'
                              }`}
                              title="Supprimer"
                            >
                              <i className="fas fa-trash"></i>
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Empty State */}
            {articles.length === 0 && (
              <div className={`text-center py-12 ${isDarkMode ? 'text-gray-300' : 'text-gray-900'}`}>
                <div className={`w-20 h-20 rounded-full flex items-center justify-center mx-auto mb-4 ${
                  isDarkMode ? 'bg-gray-700' : 'bg-gray-100'
                }`}>
                  <i className={`fas fa-fire-extinguisher text-2xl ${
                    isDarkMode ? 'text-gray-400' : 'text-gray-400'
                  }`}></i>
                </div>
                <h3 className={`text-lg font-semibold mb-2 ${
                  isDarkMode ? 'text-white' : 'text-gray-900'
                }`}>Aucun article trouvé</h3>
                <p className={isDarkMode ? 'text-gray-400' : 'text-gray-500'}>
                  Commencez par ajouter votre premier article.
                </p>
              </div>
            )}

            {/* Pagination */}
            {articles.length > 0 && (
              <div className="mt-6">
                <Pagination
                  currentPage={pagination.page}
                  totalPages={pagination.maxPage}
                  pageSize={pagination.pageSize}
                  totalItems={pagination.total}
                  onPageChange={onPageChange}
                  onPageSizeChange={onPageSizeChange}
                />
              </div>
            )}
          </>
        )}
      </div>
    </Card>
  );
};

export default ArticlesList;
